import type { ReactElement } from "react"

import { Check, Close } from "@mui/icons-material"
import { Grid } from "@mui/material"
import { Typography } from "@synapse-analytics/synapse-ui"

import styles from "./Tick.module.scss"

/**
 * Mapping of variant types to their corresponding icon components
 * @constant {Record<string, JSX.Element>}
 */
const disc = {
  positive: <Check className={styles.Icon} />,
  base: <Close className={styles.Icon} />,
}

type TickProps = {
  /**
   * Determines which icon to display and the text color
   * - 'positive' shows a check mark
   * - 'base' shows a close/x mark
   */
  variant: "positive" | "base"

  /**
   * Optional text to display next to the icon
   */
  text?: string

  /**
   * Optional id prop
   */
  id?: string
}

/**
 * A component that displays either a check or close icon with optional accompanying text.
 * Used to indicate positive or base status in the UI.
 *
 * @param {'positive' | 'base'} variant - Determines which icon to display and the text color
 * @param {string} [text] - Optional text to display next to the icon
 * @returns {ReactElement} An MUI Grid container with an icon and optional text
 */
export function Tick(props: Readonly<TickProps>): ReactElement {
  const { variant, text, id } = props

  return (
    <Grid container gap={0.5} alignItems="center" id={id} data-testid={id}>
      <Grid className={styles.DiscIcon} aria-label={`${variant}-tick`}>
        {disc[variant]}
      </Grid>

      {!!text && (
        <Typography variant="label-regular" color={variant} variantColor={2}>
          {text}
        </Typography>
      )}
    </Grid>
  )
}
